import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { translateInto, translateSubtree } from '@theme/utilities';

const selectors = {
  PRICE: '[ref="priceContainer"]',
  BADGE: '[ref="badge"]',
  QUICK_ADD: 'quick-add-component',
  QUICK_ADD_BUTTON: '.quick-add__button',
};

/**
 * Product card with ref-based DOM updates.
 * Listens for `linked-color:change` events from child swatches
 * to swap images, title, price, badge, and links. The visible title uses the selected
 * product's market title (`content.name_<country>`); a11y names keep the colour-specific `product.title`.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} figureLink
 * @property {HTMLElement} [figure]
 * @property {HTMLAnchorElement} [titleLink]
 *
 * @extends {Component<Refs>}
 */
export class ProductCardNative extends Component {
  requiredRefs = ['figureLink'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.linkedColorChange, this.#handleLinkedColorChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.linkedColorChange, this.#handleLinkedColorChange);
  }

  /** @param {CustomEvent} event */
  #handleLinkedColorChange = (event) => {
    const { detail } = event;
    if (!detail) return;

    this.#updateLinks(detail);
    this.#updateImages(detail);
    this.#updateTitle(detail);
    this.#updatePrice(detail);
    this.#updateBadge(detail);
    this.#updateQuickAdd(detail);
  };

  /** @param {Record<string, string>} detail */
  #updateLinks(detail) {
    const { figureLink, titleLink } = this.refs;
    figureLink.href = detail.productUrl ?? '';
    // Chrome derives the image link's accessible name from `title` — keep it on the selected colour.
    if (detail.productTitle) figureLink.title = detail.productTitle;
    if (titleLink) titleLink.href = detail.productUrl ?? '';
  }

  /** @param {Record<string, string>} detail */
  async #updateImages(detail) {
    const { figure } = this.refs;
    if (!figure || !detail.figureHtml) return;

    const temp = Object.assign(document.createElement('div'), {
      innerHTML: detail.figureHtml,
    });

    await Promise.all([...temp.querySelectorAll('img')].map((img) => img.decode().catch(() => {})));
    figure.replaceChildren(...temp.childNodes);
  }

  /** @param {Record<string, string>} detail */
  #updateTitle(detail) {
    const { titleLink } = this.refs;
    if (!titleLink || !detail.productMarketTitle) return;

    // Old translated title stays visible until this resolves — no flash of source text.
    translateInto(titleLink, detail.productMarketTitle);
  }

  /** @param {Record<string, string>} detail */
  #updatePrice(detail) {
    if (!detail.priceHtml) return;

    const el = this.querySelector(selectors.PRICE);
    if (!el) return;

    el.outerHTML = detail.priceHtml;
    // Re-query the injected source-locale node and translate it.
    const newEl = this.querySelector(selectors.PRICE);
    if (newEl) translateSubtree(newEl);
  }

  /** @param {Record<string, string>} detail */
  #updateBadge(detail) {
    if (!detail.badgeHtml) return;

    const el = this.querySelector(selectors.BADGE);
    if (!el) return;

    el.outerHTML = detail.badgeHtml;
    const newEl = this.querySelector(selectors.BADGE);
    if (newEl) translateSubtree(newEl);
  }

  /** @param {Record<string, string>} detail */
  #updateQuickAdd(detail) {
    const quickAddEl = this.querySelector(selectors.QUICK_ADD);
    if (!(quickAddEl instanceof HTMLElement)) return;

    const handle = detail.productHandle ?? '';
    if (handle) quickAddEl.dataset.productHandle = handle;
    if (detail.productTitle) {
      this.#updateQuickAddLabel(quickAddEl, detail.productTitle);
      quickAddEl.dataset.productTitle = detail.productTitle;
    }
    delete quickAddEl.dataset.productVariant;
  }

  /**
   * Swaps only the title inside the button label so the translated prefix ("Choose: ") survives;
   * no-op if the label no longer contains the previous title (e.g. rewritten by Weglot).
   * @param {HTMLElement} quickAddEl
   * @param {string} productTitle
   */
  #updateQuickAddLabel(quickAddEl, productTitle) {
    const buttonEl = quickAddEl.querySelector(selectors.QUICK_ADD_BUTTON);
    const previousTitle = quickAddEl.dataset.productTitle;
    const label = buttonEl?.getAttribute('aria-label');
    if (!buttonEl || !label || !previousTitle || !label.includes(previousTitle)) return;

    buttonEl.setAttribute(
      'aria-label',
      label.replace(previousTitle, () => productTitle),
    );
  }
}

if (!customElements.get('product-card-native')) {
  customElements.define('product-card-native', ProductCardNative);
}
